<script lang="ts">
  // Static FAQ. Q&As cover the questions test users have asked most often plus
  // the explainers that wire up surrounding features (feedback widget, account
  // deletion, bots). Edit copy here when answers shift — no data binding.
</script>

<div class="help">
  <h1>Help &amp; FAQ</h1>
  <p class="lede">
    Short answers to the questions we hear most. Still stuck? Hit the
    <strong>Tell us</strong> link in the beta banner or just reply to any email from us.
  </p>

  <details open>
    <summary>What is Beat Duel?</summary>
    <p>
      Beat Duel is a music contest web app. You join genre-based <em>duels</em>, submit a track from YouTube,
      SoundCloud, TikTok, or an upload, and vote on other people's picks. The platform AI-scores tracks across several
      dimensions (composition, hook, audio quality) so good music surfaces even if your follower count is small.
    </p>
  </details>

  <details>
    <summary>What's a duel?</summary>
    <p>
      A duel is a short-lived contest within a single genre or style. Players submit one track each, vote on the others,
      and the duel ends after its timer runs out. Top voters earn points, and the winning tracks get a permanent home on
      the leaderboard.
    </p>
  </details>

  <details>
    <summary>How does the AI scoring work?</summary>
    <p>
      Every submitted track gets analyzed across composition, hook strength, audio quality, and a handful of
      acoustic-feature signals (BPM, key, danceability, energy) when those are available. We also factor in licensing
      data and the source platform so cover videos and unofficial uploads don't out-rank official releases. Scores show
      up on the duel result page and on each track's detail view.
    </p>
  </details>

  <details>
    <summary>Who are Groove, Melody, Beat, and Rex?</summary>
    <p>
      Those are our four synthetic users (a.k.a. bots). They submit, vote, and follow inside duels based on their own
      style affinities — they're how new genres get seeded with activity while the community is small.
    </p>
  </details>

  <details>
    <summary>How do invites work?</summary>
    <p>
      Beat Duel is currently invite-only. Existing members can send invites from
      <a href="#/account/invite">their account page</a> (each member starts with a small invite quota). If you don't know
      anyone on the platform yet, you can join the waitlist from the homepage — we approve in batches.
    </p>
  </details>

  <details>
    <summary>What's the leaderboard and how do I earn points?</summary>
    <p>
      Points come from voting accurately (top-voting the winning track), submitting tracks that get votes, playing the
      mini-games, and a few other small actions. The leaderboard shows the current week; your all-time totals live on
      your account page. The header rank at the top of every page is week-scoped — the <code>W1</code>,
      <code>W2</code>, … tag next to the points count tells you which week.
    </p>
  </details>

  <details>
    <summary>What's the &ldquo;Official&rdquo; badge on some tracks?</summary>
    <p>
      That green badge appears on YouTube tracks where the platform reports them as licensed content (typically an
      official label upload or VEVO channel). It's a hint that the track is the canonical release, not a re-upload, fan
      cover, or rip.
    </p>
  </details>

  <details>
    <summary>How do I report a problem or share an idea?</summary>
    <p>
      The fastest path is the <strong>Tell us</strong> link in the beta banner at the top of the page. It opens a one-field
      form — type whatever's on your mind. We read every submission. You can also reply directly to any transactional email
      from us; those go straight to ops.
    </p>
  </details>

  <details>
    <summary>How do I delete my account?</summary>
    <p>
      Open <a href="#/account/security">Account &middot; Security</a> and use the &ldquo;Delete account&rdquo; option at the
      bottom. We soft-delete first (your username and email are reserved so they can't be reclaimed by impersonators) and
      hard-purge after the retention window expires.
    </p>
  </details>

  <details>
    <summary>Is Beat Duel free?</summary>
    <p>
      Yes — the core experience (joining duels, submitting, voting, mini-games) is free during the private-beta period.
      We're experimenting with optional perks for verified artists; nothing the average listener uses is gated behind a
      paywall.
    </p>
  </details>

  <p class="cta">
    Didn't see your question? Hit <strong>Tell us</strong> in the beta banner — we'll write the answer here next.
  </p>
</div>

<style>
  .help {
    max-width: 720px;
    margin: 0 auto;
    padding: 2rem 1rem 4rem;
    color: var(--text);
    line-height: 1.55;
  }
  .help h1 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }
  .lede {
    margin: 0 0 1.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
  }
  details {
    border-top: 1px solid var(--border);
    padding: 0.85rem 0;
  }
  details:last-of-type {
    border-bottom: 1px solid var(--border);
  }
  details summary {
    cursor: pointer;
    font-weight: 600;
    font-size: 1rem;
    list-style: none;
    position: relative;
    padding-right: 1.5rem;
  }
  details summary::-webkit-details-marker {
    display: none;
  }
  details summary::after {
    content: '+';
    position: absolute;
    right: 0;
    top: 0;
    font-weight: 400;
    color: var(--text-muted);
    transition: transform 0.15s;
  }
  details[open] summary::after {
    content: '−';
  }
  details p {
    margin: 0.65rem 0 0;
    color: var(--text-muted);
    font-size: 0.93rem;
  }
  details a {
    color: var(--cyan);
  }
  .cta {
    margin-top: 2rem;
    padding: 1rem;
    border-radius: 8px;
    background: rgba(201, 52, 148, 0.08);
    border: 1px solid rgba(201, 52, 148, 0.25);
    text-align: center;
    color: var(--text);
    font-size: 0.9rem;
  }
</style>
